import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Image from '../../../../helpers/Image/Image'
import { agnostifyUrl } from '../../../../helpers/utils'
import { createGolContentDataAttributes } from '../../../../helpers/tracking'
import * as Styled from './M0043.styles'

/**
 * - This is a promotional card designed to sit within a grid or listing of product cards.
 * - It can have up to three images showing when hovering over the component.
 * */
const M0043 = ({
  backgroundColor,
  bodyCopy,
  ctaCopy,
  gridClasses,
  heading,
  linkTarget,
  linkUrl,
  linkTitle,
  mainImage,
  onLinkClick,
  secondaryImage,
  tertiaryImage,
  voucherCode,
  consumingGridType,
  positionInGrid
}) => {
  const getGOLDataAttributes = (action) =>
    createGolContentDataAttributes(
      consumingGridType,
      `M0043 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      linkTitle,
      linkUrl,
      heading,
      action
    )

  const addGolTracking = (event) => {
    const dataAttributes = getGOLDataAttributes()

    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const onLinkClickFn = (e) => (typeof onLinkClick === 'function' ? onLinkClick(e, agnostifyUrl(linkUrl)) : false)

  const ctaClick = (event) => {
    if (onLinkClick) {
      onLinkClickFn(event, agnostifyUrl(linkUrl))
    }
    addGolTracking(event)
  }

  const [hoverActive, setHoverActive] = useState(false)

  const onEnter = () => {
    setHoverActive(true)
  }

  const onLeave = () => {
    setHoverActive(false)
  }

  return (
    <Styled.Anchor
      {...getGOLDataAttributes('Load')}
      data-test='m0043-container'
      href={agnostifyUrl(linkUrl)}
      title={linkTitle}
      className={gridClasses || ''}
      onClick={(e) => ctaClick(e)}
      onMouseEnter={onEnter}
      onTouchStart={onEnter}
      onMouseLeave={onLeave}
      onTouchEnd={onLeave}
      target={linkTarget}
      textColor={backgroundColor || 'grey-dark'}
    >
      <Styled.ImageContainer data-test='m0043-image-container'>
        <Styled.Images data-test='m0043-images-container' active={hoverActive}>
          <Styled.ImageWrapper data-test='m0043-image-wrapper-1' imageNumber={1}>
            <Image alt={mainImage.altText} src={mainImage.source} data-test='m0043-image-1' />
          </Styled.ImageWrapper>
          {Object.keys(secondaryImage).length > 0 && (
            <Styled.ImageWrapper data-test='m0043-image-wrapper-2' imageNumber={2}>
              <Image alt={secondaryImage.altText} src={secondaryImage.source} data-test='m0043-image-2' />
            </Styled.ImageWrapper>
          )}
          {Object.keys(tertiaryImage).length > 0 && (
            <Styled.ImageWrapper data-test='m0043-image-wrapper-3' imageNumber={3}>
              <Image alt={tertiaryImage.altText} src={tertiaryImage.source} data-test='m0043-image-3' />
            </Styled.ImageWrapper>
          )}
        </Styled.Images>
      </Styled.ImageContainer>
      <Styled.Details data-test='m0043-text-container'>
        <p>
          <strong data-test='m0043-heading'>{heading}</strong>
        </p>

        {bodyCopy && <Styled.DetailSub data-test='m0043-bodyCopy'>{bodyCopy}</Styled.DetailSub>}
        {voucherCode && (
          <Styled.DetailSub data-test='m0043-bodyCopy'>
            <strong data-test='m0043-voucher'>{voucherCode}</strong>
          </Styled.DetailSub>
        )}
      </Styled.Details>
      <Styled.Bottom>
        {ctaCopy && (
          <p data-test='m0043-cta-container'>
            <strong data-test='m0043-ctaCopy' className='a'>
              {ctaCopy}
            </strong>
          </p>
        )}
      </Styled.Bottom>
    </Styled.Anchor>
  )
}

M0043.propTypes = {
  /** Optional color of the background, white by default */
  backgroundColor: PropTypes.string,
  /** Optional text for the body */
  bodyCopy: PropTypes.string,
  /** Optional text for the CTA */
  ctaCopy: PropTypes.string,
  /** Optional grid class for the className */
  gridClasses: PropTypes.string,
  /** Optional text for heading */
  heading: PropTypes.string,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional link title, can be seen when hovering over the component */
  linkTitle: PropTypes.string,
  /** Optional link url */
  linkUrl: PropTypes.string,
  /** Optional image */
  mainImage: PropTypes.object,
  /** Optional function to handle the click on the component */
  onLinkClick: PropTypes.func,
  /** Optional secondary image, appears when hovering over the component after the main image */
  secondaryImage: PropTypes.object,
  /** Optional tertiary image, appears when hovering over the component after the secondary image */
  tertiaryImage: PropTypes.object,
  /** Optional voucher code text */
  voucherCode: PropTypes.string
}

M0043.defaultProps = {
  backgroundColor: 'white',
  bodyCopy: '',
  ctaCopy: '',
  gridClasses: '',
  heading: '',
  linkTarget: '',
  linkTitle: '',
  linkUrl: '',
  mainImage: {},
  onLinkClick: null,
  secondaryImage: {},
  tertiaryImage: {},
  voucherCode: ''
}

export default M0043
